import React, { useState, useRef, useEffect } from "react";
import { Button, Box, Typography, Paper, Grid, CircularProgress } from "@mui/material";
// import ReactDOMServer from "react-dom/server";
import { useTenantHeader } from "../../hooks/useTenantHeader";
import { usePdfGenerator } from "../../hooks/usePdfGenerator";
// import { useApplicationPhotoInfo } from "../../hooks/useApplicationForm";
import { useStudentPhoto, useStudentSignature } from "../../hooks/useStudents";
import { useSchools } from "../../hooks/useSchools";
import { useAuth } from "../../contexts/AuthContext";
import { useSchoolIdForAdmin } from "../../hooks/useSchoolIdForAdmin";
// we'll send a complete HTML string instead of SSR JSX
import { getAadharCardCertificateHtml } from "./AadharCardCertificateHtmlTemplate";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { Download, Image, Person } from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";

interface AadhaarCertificatePrintFormProps {
  data: {
    id?: number; // Application ID for fetching photo
    // Optional: pre-fetched URLs (used for bulk generation to avoid per-student API calls)
    photoUrl?: string | null;
    signatureUrl?: string | null;
    // When true, template will NOT call per-student media APIs (bulk mode).
    // Use this even when photoUrl/signatureUrl are null (e.g., no photo).
    skipStudentMediaFetch?: boolean;
    dateOfIssue: string;
    isResident: boolean;
    isNRI: boolean;
    isNewEnrolment: boolean;
    isUpdateRequest: boolean;
    aadhaarNumber: string;
    fullName: string;
    careOf: string;
    houseNo: string;
    street: string;
    landmark: string;
    area: string;
    village: string;
    postOffice: string;
    district: string;
    state: string;
    pinCode: string;
    dateOfBirth: string; // YYYY-MM-DD
    mobileNo: string;
    gmail: string;
    certifierName: string;
    certifierDesignation: string;
    certifierOfficeAddress: string;
    certifierContactNumber: string;
    isGazettedOfficer: boolean;
    isHeadOfInstitution: boolean;
  };
  hidePrintButton?: boolean;
  schoolData?: any; // School data for certifier details
}

const sanitize = (s = "") => (s || "").toUpperCase().replace(/[^A-Z0-9\s@./-]/g, "");

// Enhanced sanitize function for office address that preserves periods within words
const sanitizeOfficeAddress = (s = "") => {
  if (!s) return "";

  // Convert to uppercase
  let result = s.toUpperCase();

  // Remove unwanted characters but preserve periods, spaces, and common punctuation
  result = result.replace(/[^A-Z0-9\s@./-]/g, "");

  // Ensure periods are not separated from their preceding characters
  // This handles cases like "H. S." where we want to keep the period with the letter
  result = result.replace(/\s+\./g, ".");

  return result;
};

// Helper function to split office address intelligently across two lines
const splitOfficeAddress = (address: string, maxLength: number = 31) => {
  if (!address || address.length <= maxLength) {
    return { firstLine: address, secondLine: "" };
  }

  // Try to split at word boundaries
  const words = address.split(/\s+/);
  let firstLine = "";
  let secondLine = "";

  for (const word of words) {
    if ((firstLine + " " + word).trim().length <= maxLength) {
      firstLine = (firstLine + " " + word).trim();
    } else {
      secondLine = (secondLine + " " + word).trim();
    }
  }

  return { firstLine, secondLine };
};

const splitYMD = (iso: string | Date | null | undefined | number[]) => {

  if (!iso) {
    return { d: "", m: "", y: "" };
  }

  // Handle array format dates [year, month, day] - common from APIs
  if (Array.isArray(iso) && iso.length >= 3) {
    const [year, month, day] = iso;
    if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
      // Convert to YYYY-MM-DD format
      // Note: Backend sends month as 1-indexed (1=Jan, 12=Dec), so we use it directly
      const yearStr = year.toString().padStart(4, '0');
      const monthStr = month.toString().padStart(2, '0');
      const dayStr = day.toString().padStart(2, '0');

      // Return the result directly without creating a Date object to avoid timezone issues
      const result = { d: dayStr, m: monthStr, y: yearStr };
      return result;
    }
  }

  // Convert Date object to string if needed
  let dateString: string;
  if (iso instanceof Date) {
    dateString = iso.toISOString().split('T')[0];
  } else if (typeof iso === 'string') {
    dateString = iso;
  } else {
    return { d: "", m: "", y: "" };
  }

  // Try to parse the date string if it's not in YYYY-MM-DD format
  if (!dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    try {
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        dateString = parsedDate.toISOString().split('T')[0];
      } else {
        return { d: "", m: "", y: "" };
      }
    } catch (error) {
      return { d: "", m: "", y: "" };
    }
  }

  // Final validation
  if (!dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return { d: "", m: "", y: "" };
  }

  const [y, m, d] = dateString.split("-");
  const result = { d: d || "", m: m || "", y: y || "" };
  return result;
};

// Enhanced CharBoxes component matching official format
function CharBoxes({
  value = "",
  max,
  size = 25,
  gap = 0,
  bold = true,
  useOfficeAddressSanitization = false
}: {
  value?: string;
  max: number;
  size?: number;
  gap?: number;
  bold?: boolean;
  useOfficeAddressSanitization?: boolean;
}) {
  const sanitizedValue = useOfficeAddressSanitization ? sanitizeOfficeAddress(value) : sanitize(value);
  const chars = sanitizedValue.padEnd(max, " ").slice(0, max).split("");

  return (
    <Box sx={{ display: "inline-flex", gap: `${gap}px` }}>
      {chars.map((char, index) => (
        <Box
          key={index}
          sx={{
            width: `${size}px`,
            height: `${Math.floor(size * 1.2)}px`,
            border: "1px solid #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "19px",
            fontFamily: "Arial, sans-serif",
            fontWeight: "normal",
            backgroundColor: "#fff",
            lineHeight: 1,
          }}
        >
          {char.trim()}
        </Box>
      ))}
    </Box>
  );
}

// Updated Row component
const Row: React.FC<{
  label: React.ReactNode;
  children: React.ReactNode;
  width?: string;
  style?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
}> = ({ label, children, width = "120px" }) => (
  <Box sx={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
    <Typography sx={{ fontSize: "10px", width: width, fontWeight: "bold" }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

// Updated Tick component
const Tick: React.FC<{
  checked?: boolean;
  label: string;
  onClick?: () => void;
}> = ({ checked, label, onClick }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "4px",
      cursor: onClick ? "pointer" : "default",
    }}
    onClick={onClick}
  >
    <Box
      sx={{
        width: "12px",
        height: "12px",
        border: "1px solid #000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        backgroundColor: checked ? "#000" : "#fff",
        color: checked ? "#fff" : "#000",
        transition: "all 0.2s ease",
        "&:hover": onClick ? {
          transform: "scale(1.1)",
        } : {},
      }}
    >
      {checked ? "✓" : ""}
    </Box>
    <Typography sx={{ fontSize: "13px", userSelect: "none" }}>
      {label}
    </Typography>
  </Box>
);

// Editable Date Box component for day/month (2 digits) or year (4 digits)
const EditableDateBox: React.FC<{
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  placeholder: string;
  width?: string;
  hight?: string;
  type?: "day" | "month" | "year";
  style?: React.CSSProperties;
}> = ({
  value,
  onChange,
  maxLength,
  placeholder,
  width = "7mm",
  hight = "6mm",
  type
}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value.replace(/[^0-9]/g, ""); // Only allow numbers
      if (newValue.length <= maxLength) {
        onChange(newValue);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select(); // Select all text when focused
    };

    const handleBlur = () => {
      // Auto-format the value when user leaves the field
      if (value) {
        const numValue = parseInt(value);
        if (type === "day" && numValue >= 1 && numValue <= 31) {
          onChange(numValue.toString().padStart(2, "0"));
        } else if (type === "month" && numValue >= 1 && numValue <= 12) {
          onChange(numValue.toString().padStart(2, "0"));
        } else if (type === "year" && numValue >= 1900) {
          onChange(numValue.toString().padStart(4, "0"));
        }
      }
    };

    // Determine if the value is valid for styling
    const isValid = () => {
      if (!value) return true; // Empty is considered valid (will show placeholder)
      const numValue = parseInt(value);
      switch (type) {
        case "day":
          return numValue >= 1 && numValue <= 31;
        case "month":
          return numValue >= 1 && numValue <= 12;
        case "year":
          return numValue >= 1900 && numValue <= new Date().getFullYear() + 10;
        default:
          return true;
      }
    };

    return (
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        style={{
          width: width,
          height: hight,
          border: `2px solid ${isValid() ? "#000" : "#dc2626"}`,
          textAlign: "center",
          fontSize: "13px",
          fontWeight: "bold",
          fontFamily: "Arial, sans-serif",
          backgroundColor: isValid() ? "#fff" : "#fef2f2",
          outline: "none",
          padding: "0",
          margin: "0",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
      />
    );
  };

const AadharCardTemplate: React.FC<AadhaarCertificatePrintFormProps> = ({
  data,
  hidePrintButton = false,
  schoolData: propSchoolData
}) => {
  const { t } = useTranslation();
  const { tenantLoading } = useTenantHeader();
  const { generatePdf, isGenerating } = usePdfGenerator();
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdfFromImage, setIsDownloadingPdfFromImage] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);

  // School data fetching - use prop if available, otherwise fetch
  const { user, isSchoolAdmin } = useAuth();
  const shouldFetchAdminSchoolId = !!user && isSchoolAdmin() && !user.schoolId;
  const adminSchoolIdQuery = useSchoolIdForAdmin(shouldFetchAdminSchoolId ? user?.id : undefined);
  const effectiveSchoolId = user?.schoolId ?? adminSchoolIdQuery.data ?? undefined;
  const { useSchoolById } = useSchools(false, { disableSchoolAdmins: true });
  const { data: fetchedSchoolData, isLoading: schoolLoading } = useSchoolById(effectiveSchoolId);

  // Use prop school data if available, otherwise use fetched data
  const schoolData = propSchoolData || fetchedSchoolData;

  // Photo functionality using the new hook (disabled when pre-fetched URL is provided)
  const shouldFetchPhoto = !!data?.id && !data?.photoUrl && !data?.skipStudentMediaFetch;
  const studentIdForPhotoFetch = shouldFetchPhoto ? data?.id : undefined;
  const { imageUrl: authenticatedImageUrl, isLoading: photoLoading, error: imageError, hasPhoto } =
    useStudentPhoto(studentIdForPhotoFetch);

  // Signature functionality using the new hook
  const shouldFetchSignature = !!data?.id && !data?.signatureUrl && !data?.skipStudentMediaFetch;
  const studentIdForSignatureFetch = shouldFetchSignature ? data?.id : undefined;
  const { signatureUrl: authenticatedSignatureUrl, isLoading: signatureLoading, error: signatureError, hasSignature } =
    useStudentSignature(studentIdForSignatureFetch);

  // Test the specific data format you're receiving
  if (Array.isArray(data.dateOfBirth)) {
    const [year, month, day] = data.dateOfBirth;
  }

  if (Array.isArray(data.dateOfIssue)) {
    const [year, month, day] = data.dateOfIssue;
  }

  const { d: dobD, m: dobM, y: dobY } = splitYMD(data.dateOfBirth);
  const { d: doiD, m: doiM, y: doiY } = splitYMD(data.dateOfIssue);

  // Fallback for empty date of birth - use current date as placeholder
  const finalDobD = dobD || t("dd");
  const finalDobM = dobM || t("mm");
  const finalDobY = dobY || t("yyyy");

  // Check if we have actual date data or just placeholders
  const hasActualDate = dobD && dobM && dobY;

  // Test function to verify date parsing (can be removed in production)
  const testDateParsing = () => {
    const testDates = [
      "1990-05-15",
      "1990/05/15",
      "15/05/1990",
      "15-05-1990",
      [2025, 9, 5], // Array format like from your API
      [1990, 5, 15], // Another array format
      new Date("1990-05-15"),
      "invalid-date",
      null,
      undefined,
      ""
    ];

    testDates.forEach((testDate, index) => {
      const result = splitYMD(testDate);
    });
  };

  // Uncomment the line below to test date parsing
  // testDateParsing();

  // State for interactive checkboxes - defaulting to false (unchecked)
  const [isResident, setIsResident] = useState(false);
  const [isNRI, setIsNRI] = useState(false);
  const [isNewEnrolment, setIsNewEnrolment] = useState(false);
  const [isUpdateRequest, setIsUpdateRequest] = useState(false);
  const [isGazettedOfficer, setIsGazettedOfficer] = useState(false);
  const [isHeadOfInstitution, setIsHeadOfInstitution] = useState(data.isHeadOfInstitution || false);

  // Update isHeadOfInstitution when data prop changes (for bulk download)
  useEffect(() => {
    setIsHeadOfInstitution(data.isHeadOfInstitution || false);
  }, [data.isHeadOfInstitution]);

  // Additional state for all other checkboxes
  const [isVillagePanchayatHead, setIsVillagePanchayatHead] = useState(false);
  const [isGazettedOfficerGroupB, setIsGazettedOfficerGroupB] = useState(false);
  const [isMPMLAMLC, setIsMPMLAMLC] = useState(false);
  const [isTehsildar, setIsTehsildar] = useState(false);
  const [isSuperintendentWarden, setIsSuperintendentWarden] = useState(false);
  const [isEPFOOfficer, setIsEPFOOfficer] = useState(false);

  // Checklist checkboxes
  const [isNoOverwriting, setIsNoOverwriting] = useState(false);
  const [isIssueDateFilled, setIsIssueDateFilled] = useState(false);
  const [isResidentsSignature, setIsResidentsSignature] = useState(false);
  const [isCertifiersDetails, setIsCertifiersDetails] = useState(false);
  const [isResidentsPhotoCrossSigned, setIsResidentsPhotoCrossSigned] = useState(false);

  // State for editable date fields - initialize from data or with current date
  const [dateDay, setDateDay] = useState(
    doiD || new Date().toISOString().split("T")[0].split("-")[2]
  );
  const [dateMonth, setDateMonth] = useState(
    doiM || new Date().toISOString().split("T")[0].split("-")[1]
  );
  const [dateYear, setDateYear] = useState(
    doiY || new Date().toISOString().split("T")[0].split("-")[0]
  );

  // Function to download template as image
  const downloadAsImage = async () => {
    if (!templateRef.current) return;

    setIsDownloadingImage(true);
    try {
      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: true,
        background: '#ffffff',
        logging: false,
      });

      const link = document.createElement('a');
      link.download = `aadhaar-certificate-${sanitize(data.fullName)}-${data.dateOfBirth}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      alert(t('failed_download_image'));
    } finally {
      setIsDownloadingImage(false);
    }
  };

  // Function to download template as PDF from image
  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;

    setIsDownloadingPdfFromImage(true);
    try {
      const element = templateRef.current;
      const opt = {
        margin: [0.05, 0.05, 0.05, 0.05],
        filename: `aadhaar-certificate-${sanitize(data.fullName)}-${data.dateOfBirth}-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 1.2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: element.offsetWidth,
          height: element.offsetHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: element.offsetWidth,
          windowHeight: element.offsetHeight
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait',
          compress: true,
          putOnlyUsedFonts: true
        },
        pagebreak: { mode: 'avoid-all', before: '.page-break' }
      };

      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      alert(t('failed_download_pdf'));
    } finally {
      setIsDownloadingPdfFromImage(false);
    }
  };

  const handleGeneratePdf = () => {
    // fold editable DOI boxes back into an ISO date (YYYY-MM-DD)
    const doiIso =
      dateYear && dateMonth && dateDay
        ? `${String(dateYear).padStart(4, "0")}-${String(dateMonth).padStart(2, "0")}-${String(dateDay).padStart(2, "0")}`
        : (data.dateOfIssue || "");

    // build complete HTML + <style> using your helper
    const htmlToSend = getAadharCardCertificateHtml({
      ...data,
      dateOfIssue: doiIso,
      // reflect the user's current checkbox selections in the PDF
      isResident,
      isNRI,
      isNewEnrolment,
      isUpdateRequest,
      isGazettedOfficer,
      isHeadOfInstitution,
      isVillagePanchayatHead,
      isGazettedOfficerGroupB,
      isMPMLAMLC,
      isTehsildar,
      isSuperintendentWarden,
      isEPFOOfficer,
      isNoOverwriting,
      isIssueDateFilled,
      isResidentsSignature,
      isCertifiersDetails,
      isResidentsPhotoCrossSigned,
    });

    // Check if HTML generation was successful
    if (!htmlToSend) {
      alert(t('failed_generate_aadhar_card'));
      return;
    }

    generatePdf({
      htmlContent: htmlToSend,
      fileName: `aadhaar-certificate-${sanitize(data.fullName)}-${data.dateOfBirth}.pdf`,
    });
  };

  return (
    <div>
      <Paper
        ref={templateRef}
        sx={{
          width: "210mm",
          height: "297mm",
          padding: "4mm",
          margin: "0 auto",
          border: "1px solid #CFCFCF",
          backgroundColor: "#fff",
          fontFamily: "Arial, sans-serif",
          fontSize: "9px",
          lineHeight: "1.1",
          overflow: "hidden",
          "@media print": {
            margin: 0,
            border: "none",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#dc2626",
            color: "white",
            padding: "4px",
            textAlign: "center",
            marginBottom: "1px",
          position: 'sticky',
          top: 0,
          zIndex: 100,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Arial, sans-serif",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {t('certificate_for_aadhaar_enrolment')}
          </Typography>
        </Box>

        {/* Instructions Section */}
        <Box
          sx={{
            display: "flex",
            padding: "0.3mm",
            border: "1px solid #000",
            marginBottom: "1px",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: "10px", fontWeight: "bold", marginBottom: "0.5mm" }}
            >
              <Box component="span" sx={{ fontStyle: "italic" }}>
                {t('instructions')}
              </Box>{" "}
              {t('all_details_block_letters')}
            </Typography>
            <Typography sx={{ fontSize: "8px", marginBottom: "0.5mm" }}>
              {t('printed_plain_a4')}
            </Typography>
            <Typography sx={{ fontSize: "8px" }}>
              {t('not_required_letter_head')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "8px", fontStyle: "italic", marginBottom: "0.5mm" }}
            >
              {t('valid_3_months')}
            </Typography>
            <Typography
              sx={{
                fontSize: "8px",
                fontWeight: "bold",
                marginBottom: "0.5mm",
                marginRight: "0.5mm",
              }}
            >
              {t('date_of_issue')}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: "2px",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <EditableDateBox
                value={dateDay}
                onChange={setDateDay}
                maxLength={2}
                placeholder={t("dd")}
                width="10mm"
                type="day"
              />
              <Typography sx={{ fontSize: "11px", alignSelf: "center", mx: "1mm" }}>
                /
              </Typography>
              <EditableDateBox
                value={dateMonth}
                onChange={setDateMonth}
                maxLength={2}
                placeholder={t("mm")}
                width="10mm"
                type="month"
              />
              <Typography sx={{ fontSize: "11px", alignSelf: "center", mx: "1mm" }}>
                /
              </Typography>
              <EditableDateBox
                value={dateYear}
                onChange={setDateYear}
                maxLength={4}
                placeholder={t("yyyy")}
                width="15mm"
                type="year"
              />
            </Box>
          </Box>
        </Box>

        {/* Resident Details Header */}
        <Box
          sx={{
            backgroundColor: "#dc2626",
            color: "white",
            padding: "4px 4px",
            textAlign: "center",
            marginBottom: "2px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Arial, sans-serif",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {t('residents_details')}
          </Typography>
        </Box>

        {/* Resident Type Checkboxes */}
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            padding: "8px",
            border: "1px solid #000",
            marginBottom: "2px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Tick
            checked={isResident}
            label={t('resident')}
            onClick={() => setIsResident(!isResident)}
          />
          <Tick
            checked={isNRI}
            label={t('non_resident_indian')}
            onClick={() => setIsNRI(!isNRI)}
          />
          <Tick
            checked={isNewEnrolment}
            label={t('new_enrolment')}
            onClick={() => setIsNewEnrolment(!isNewEnrolment)}
          />
          <Tick
            checked={isUpdateRequest}
            label={t('update_request')}
            onClick={() => setIsUpdateRequest(!isUpdateRequest)}
          />
        </Box>

        {/* Main Form Section - Two Column Layout */}
        <Box
          sx={{
            display: "flex",
            gap: "3mm",
            padding: "4mm",
            border: "1px solid #000",
          }}
        >
          {/* Left Column - Form Fields */}
          <Box sx={{ flex: 1, maxWidth: "100%", maxHeight: "105mm" }}>
            {/* Aadhaar Number */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('aadhaar_number')}</Typography>}
              width="40mm"
            >
              <Box sx={{ display: "flex", gap: "2mm", alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "row", gap: "2mm" }}>
                  <CharBoxes value={(data.aadhaarNumber || "").slice(0, 4)} max={4} size={19} />
                  <CharBoxes value={(data.aadhaarNumber || "").slice(4, 8)} max={4} size={19} />
                  <CharBoxes value={(data.aadhaarNumber || "").slice(8, 12)} max={4} size={19} />
                </div>
                <Typography sx={{ fontSize: "15px", marginLeft: "1mm", color: "#666" }}>
                  {t('for_update_only')}
                </Typography>
              </Box>
            </Row>

            {/* Full Name */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('full_name')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.fullName || ""} max={31} size={18} />
            </Row>

            {/* C/o */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('care_of')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.careOf || ""} max={31} size={18} />
            </Row>

            {/* House No / Bldg / Apt */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('house_no_bldg_apt')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.houseNo || ""} max={31} size={18} />
            </Row>

            {/* Street / Road / Lane */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('street_road_lane')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.street || ""} max={31} size={18} />
            </Row>

            {/* Landmark */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('landmark')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.landmark || ""} max={31} size={18} />
            </Row>

            {/* Area / Locality / Sector */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('area_locality_sector')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.area || ""} max={31} size={18} />
            </Row>

            {/* Village / Town / City */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('village_town_city')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.village || ""} max={31} size={18} />
            </Row>

            {/* Post Office */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('post_office')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.postOffice || ""} max={31} size={18} />
            </Row>

            {/* District */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('district')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.district || ""} max={23} size={18} />
            </Row>

            {/* State */}
            <Row
              label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('state')}</Typography>}
              width="40mm"
            >
              <CharBoxes value={data.state || ""} max={23} size={18} />
            </Row>

            {/* Date of Birth */}
            <Grid container spacing={2} sx={{ display: "flex", gap: "1mm" }}>
              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "row", gap: "1mm" }}>
                <div>
                  <Row
                    label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('pin_code')}</Typography>}
                    width="40mm"
                  >
                    <CharBoxes value={data.pinCode || ""} max={6} size={18} />
                  </Row>
                  <Row
                    label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('date_of_birth')}</Typography>}
                    width="40mm"
                  >
                    <Box sx={{ display: "flex", gap: "2mm", alignItems: "center" }}>
                      <CharBoxes value={finalDobD} max={2} size={19} />
                      <Typography sx={{ fontSize: "28px" }}>/</Typography>
                      <CharBoxes value={finalDobM} max={2} size={19} />
                      <Typography sx={{ fontSize: "28px" }}>/</Typography>
                      <CharBoxes value={finalDobY} max={4} size={19} />
                      {!hasActualDate && (
                        <Typography sx={{ fontSize: "10px", color: "#dc2626", marginLeft: "8px", fontStyle: "italic" }}>
                          {t('date_not_available')}
                        </Typography>
                      )}
                    </Box>
                  </Row>
                </div>
                <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "row", gap: "1mm" }}>
                  <Box
                    sx={{
                      position: "relative",
                      top: "0mm",
                      right: "-9mm",
                      width: "45mm",
                      height: "23mm",
                      border: "1px solid #000",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      backgroundColor: "#fff",
                      paddingBottom: "3mm",
                      textAlign: "center",
                      overflow: "hidden",
                    }}
                  >
                    {(() => {
                      const effectiveSignatureUrl = data?.signatureUrl || authenticatedSignatureUrl;
                      const effectiveSignatureLoading = shouldFetchSignature ? signatureLoading : false;
                      const effectiveSignatureError = shouldFetchSignature ? signatureError : false;
                      if (effectiveSignatureLoading) {
                        return (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
                            <CircularProgress size={16} />
                            <Typography sx={{ fontSize: "7px", marginTop: "2px" }}>{t("loading")}...</Typography>
                          </Box>
                        );
                      }

                      if (effectiveSignatureUrl && !effectiveSignatureError) {
                        return (
                          <img
                            src={effectiveSignatureUrl}
                            alt={t("student_signature")}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              display: "block"
                            }}
                          />
                        );
                      }

                      // Fallback when no signature is available
                      return (
                        <>
                          <Typography
                            sx={{
                              fontSize: "9px",
                              fontWeight: "bold",
                              lineHeight: 1.2,
                            }}
                          >
                            {t('signature_of_resident')}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "9px",
                              fontWeight: "bold",
                              lineHeight: 1.2,
                            }}
                          >
                            {t('thumb_finger_impression')}
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                </Grid>
                {/* Photo Area */}
                <Box
                  sx={{
                    position: "relative",
                    top: "-17mm",
                    right: "-12mm",
                    minWidth: "30mm",
                    height: "40mm",
                    border: "1px solid #000",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                    textAlign: "center",
                    overflow: "hidden",
                  }}
                >
                  {(() => {
                    const effectivePhotoUrl = data?.photoUrl || authenticatedImageUrl;
                    const effectivePhotoLoading = shouldFetchPhoto ? photoLoading : false;
                    const effectivePhotoError = shouldFetchPhoto ? imageError : false;
                    if (effectivePhotoLoading) {
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                          <CircularProgress size={20} />
                          <Typography sx={{ fontSize: "8px", marginTop: "4px" }}>{t("loading")}...</Typography>
                        </Box>
                      );
                    }

                    if (effectivePhotoUrl && !effectivePhotoError) {
                      return (
                        <img
                          src={effectivePhotoUrl}
                          alt={t("student_photo")}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block"
                          }}
                        />
                      );
                    }

                    // Fallback when no photo is available
                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#666" }}>
                        <Person sx={{ fontSize: 30, color: "#ccc", marginBottom: "2mm" }} />
                        <Typography
                          sx={{
                            fontSize: "8px",
                            fontWeight: "bold",
                            lineHeight: 1.2,
                            marginBottom: "1mm",
                          }}
                        >
                          {t('residents_recent')}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "8px",
                            fontWeight: "bold",
                            lineHeight: 1.2,
                            marginBottom: "1mm",
                          }}
                        >
                          {t('colour_photograph')}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "6px",
                            color: "#666",
                            lineHeight: 1.2,
                          }}
                        >
                          (3.5cm x 4.5cm)
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              </Grid>
            </Grid>
          </Box>
          {/* Right Column - Photo and Signature */}
          <Box
            sx={{
              width: "40mm",
              display: "flex",
              flexDirection: "column",
              gap: "3mm",
            }}
          >
            {/* Signature Area */}
          </Box>
        </Box>

        {/* Certifier Details Header */}
        <Box
          sx={{
            backgroundColor: "#dc2626",
            color: "white",
            padding: "4px 4px",
            textAlign: "center",
            marginTop: "8px",
            marginBottom: "2px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Arial, sans-serif",
              fontWeight: "bold",
              fontSize: "12px",
            }}
          >
            {t('certifiers_details')}
          </Typography>
        </Box>

        {/* Certifier Fields */}
        <Box sx={{ border: "1px solid #000", padding: "2mm" }}>
          {/* Name of the Certifier */}
          <Row
            label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('name_of_certifier')}</Typography>}
            width="40mm"
          >
            <CharBoxes
              value={isHeadOfInstitution && schoolData?.schoolPrincipalName ? schoolData.schoolPrincipalName : ""}
              max={31}
              size={19}
            />
          </Row>

          {/* Designation */}
          <Row
            label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('designation')}</Typography>}
            width="40mm"
          >
            <CharBoxes
              value={isHeadOfInstitution && schoolData?.principalDesignation ? schoolData.principalDesignation : ""}
              max={31}
              size={19}
            />
          </Row>

          {/* Office Address - Two rows as per image */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: "3mm",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: "bold",
                width: "40mm",
                whiteSpace: "nowrap",
              }}
            >
              {t('office_address')}
            </Typography>
            <Box sx={{ flex: 1 }}>
              {(() => {
                let fullAddress = "";

                if (isHeadOfInstitution && schoolData) {
                  // Construct school address from school data: schoolname + district + state + pincode
                  const addressParts = [
                    schoolData.nameofSchool,
                    schoolData.district,
                    schoolData.state,
                    schoolData.pinNo
                  ].filter(part => part && part.trim() !== ""); // Remove empty or whitespace-only parts

                  fullAddress = addressParts.join(" ");
                }
                // If not head of institution, show empty boxes

                const { firstLine, secondLine } = splitOfficeAddress(fullAddress, 31);

                return (
                  <>
                    <Box sx={{ marginBottom: "0mm" }}>
                      <CharBoxes
                        value={firstLine}
                        max={31}
                        size={19}
                        useOfficeAddressSanitization={true}
                      />
                    </Box>
                    <Box>
                      <CharBoxes
                        value={secondLine}
                        max={31}
                        size={19}
                        useOfficeAddressSanitization={true}
                      />
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Box>

          {/* Contact Number */}
          <Row
            label={<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{t('contact_number')}</Typography>}
            width="40mm"
          >
            <CharBoxes
              value={isHeadOfInstitution && schoolData?.principalMobile ? schoolData.principalMobile : ""}
              max={10}
              size={19}
            />
          </Row>

          {/* Declaration Statement */}
          <Box sx={{ marginTop: "4mm", marginBottom: "4mm" }}>
            <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
              {t('i_hereby_certify')}
            </Typography>
          </Box>

          {/* Two Column Layout for Checkboxes */}
          <Box sx={{ display: "flex", gap: "4mm" }}>
            {/* Left Column - Certifier Type Options */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "0mm" }}>
                <Tick
                  checked={isGazettedOfficer}
                  label={t('gazetted_officer_group_a')}
                  onClick={() => setIsGazettedOfficer(!isGazettedOfficer)}
                />
                <Tick
                  checked={isVillagePanchayatHead}
                  label={t('village_panchayat_head')}
                  onClick={() => setIsVillagePanchayatHead(!isVillagePanchayatHead)}
                />
                <Tick
                  checked={isGazettedOfficerGroupB}
                  label={t('gazetted_officer_group_b')}
                  onClick={() => setIsGazettedOfficerGroupB(!isGazettedOfficerGroupB)}
                />
                <Tick
                  checked={isMPMLAMLC}
                  label={t('mp_mla_mlc_municipal')}
                  onClick={() => setIsMPMLAMLC(!isMPMLAMLC)}
                />
                <Tick
                  checked={isTehsildar}
                  label={t('tehsildar')}
                  onClick={() => setIsTehsildar(!isTehsildar)}
                />
                <Tick
                  checked={isHeadOfInstitution}
                  label={t('head_recognized_educational')}
                  onClick={() => setIsHeadOfInstitution(!isHeadOfInstitution)}
                />
                <Tick
                  checked={isSuperintendentWarden}
                  label={t('superintendent_warden_matron')}
                  onClick={() => setIsSuperintendentWarden(!isSuperintendentWarden)}
                />
                <Tick
                  checked={isEPFOOfficer}
                  label={t('epfo_officer')}
                  onClick={() => setIsEPFOOfficer(!isEPFOOfficer)}
                />
              </Box>
            </Box>

            {/* Right Column - Checklist and Signature */}
            <Box
              sx={{
                width: "140mm",
                display: "flex",
                flexDirection: "row",
                gap: "1mm",
                flexWrap: "wrap",
              }}
            >
              {/* Checklist for Certifier */}
              <Box
                sx={{
                  border: "1px solid #000",
                  padding: "1mm",
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    textAlign: "center",
                    marginBottom: "3mm",
                  }}
                >
                  {t('checklist_for_certifier')}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "1mm",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-evenly", gap: "0mm", marginTop: "0mm" }}>
                    <Tick
                      checked={isNoOverwriting}
                      label={t('no_overwriting')}
                      onClick={() => setIsNoOverwriting(!isNoOverwriting)}
                    />
                    <Tick
                      checked={isIssueDateFilled}
                      label={t('issue_date_filled')}
                      onClick={() => setIsIssueDateFilled(!isIssueDateFilled)}
                    />
                    <Tick
                      checked={isResidentsSignature}
                      label={t('residents_signature')}
                      onClick={() => setIsResidentsSignature(!isResidentsSignature)}
                    />
                    <Tick
                      checked={isCertifiersDetails}
                      label={t('certifiers_details_check')}
                      onClick={() => setIsCertifiersDetails(!isCertifiersDetails)}
                    />
                  </Box>
                  <Box sx={{ marginTop: "0mm" }}>
                    <Tick
                      checked={isResidentsPhotoCrossSigned}
                      label={t('residents_photo_cross_signed')}
                      onClick={() => setIsResidentsPhotoCrossSigned(!isResidentsPhotoCrossSigned)}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Signature & Stamp Section */}
              <Box
                sx={{
                  width: "100%",
                  height: "25mm",
                  border: "1px solid #000",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  paddingBottom: "3mm",
                  backgroundColor: "#fff",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>
                  {t('signature_stamp_certifier')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* NOTE Section */}
        <Box
          sx={{
            marginTop: "0mm",
            padding: "2mm",
            fontSize: "9px",
            lineHeight: 1.3,
            textAlign: "justify",
          }}
        >
          <Typography sx={{ fontSize: "9px", fontWeight: "bold", marginBottom: "2mm" }}>
            {t('note')}{" "}
            <Typography component="span" sx={{ fontSize: "10px", fontWeight: "normal" }}>
              {t('this_format_applicable')}
            </Typography>
          </Typography>
        </Box>

      </Paper>

      {/* Download Buttons - Only show if not hidden */}
      {!hidePrintButton && (
        <Box sx={{ marginTop: "16px", textAlign: "center" }}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            {/* Image Download Button */}
            <Button
              variant="contained"
              startIcon={<Image />}
              disabled={isDownloadingImage || tenantLoading}
              onClick={downloadAsImage}
              disableRipple
              sx={{
                bgcolor: "#1976d2",
                "&:hover": {
                  bgcolor: "#1976d2",
                  boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)"
                }
              }}
            >
              {tenantLoading ? (
                t('loading')
              ) : isDownloadingImage ? (
                "Downloading..."
              ) : (
                "Download as Image"
              )}
            </Button>

            {/* PDF Download Button */}
            <Button
              variant="contained"
              startIcon={<Download />}
              disabled={isDownloadingPdfFromImage || tenantLoading}
              onClick={downloadAsPdfFromImage}
              disableRipple
              sx={{
                bgcolor: "#d32f2f",
                "&:hover": {
                  bgcolor: "#d32f2f",
                  boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)"
                }
              }}
            >
              {tenantLoading ? (
                t('loading')
              ) : isDownloadingPdfFromImage ? (
                "Downloading..."
              ) : (
                "Download as PDF"
              )}
            </Button>
          </Box>

          {/* Loading progress indicators */}
          {(isDownloadingImage || isDownloadingPdfFromImage) && (
            <Box sx={{ marginTop: "12px" }}>
              <Typography variant="body2" color="text.secondary">
                {isDownloadingImage
                  ? `${t('please_wait_aadhaar_certificate')} ${t("image")} ${t('is_being_generated')}`
                  : `${t('please_wait_aadhaar_certificate')} PDF ${t('is_being_generated')}`
                }
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default AadharCardTemplate;
